import { CoreController } from "./maincontroller.js";
class _Ridecell {
  constructor() {
    /* Ensure we always have a single instance of Ridecell class */
    if (_Ridecell._instance) {
      return _Ridecell._instance;
    }
    _Ridecell._instance = this;
    this.coreController = new CoreController();
  }

  /**
   * Initializes the Ridecell Core.
   * @param {Object} configs - Configs object.
   *
   * @returns {Settings} Settings added in Ridecell backend.
   * @memberof Ridecell
   * @example
   *
   * var configs = {
   *    base_url : "https://api.ridecell.com",
   *    auth_token : "eyJ0eXAiOiJKV1QiLCJccwVEaBABj..."
   * }
   * Ridecell.init(configs)
   * .then((tenantSettings) => {})
   * .catch((error) => {);
   *
   */
  init(configs) {
    return this.coreController.init(configs);
  }

  /**
   * Authenticates a user.
   * @param {String} email Email address for login.
   * @param {String} password Password for login.
   * @param {string} device_id device_id of user which needs to be trusted when MFA is enabled
   * @param {RidecellCallback} callback RidecellCallback for success or failure.
   *
   * @returns {Customer or Nonce} Returns the customer profile if MFA is not enabled otherwise returns nonce to be used in verification and in resending code
   * @memberof Ridecell
   *
   * @example
   *
   * If MFA is Enabled
   * Ridecell.login({ email: "xyz@ridecell.com", password: "password", device_id: "dshfk13123kjk1"})
   * .then(() => { }))
   * .catch((error) => { });
   *
   * If MFA is disabled
   * Ridecell.login({ email: "xyz@ridecell.com", password: "password"})
   * .then((customer) => { }))
   * .catch((error) => { });
   */
  login({ email, password, deviceId }) {
    return this.coreController.login({ email, password, deviceId });
  }

  /**
   * Verifies the MFA code
   * @param {String} code Verification code sent to user.
   * @param {String} nonce Nonce we got from response in login API and when MFA is enabled
   * @param {RidecellCallback} callback RidecellCallback for success or failure.
   *
   * @memberof Ridecell
   *
   * @example
   *
   * Ridecell.mfaVerify({ code: '12323', nonce: '12323'})
   * .then(() => { }))
   * .catch((error) => { });
   */
  mfaVerify({ code, nonce }) {
    return this.coreController.mfaVerify({ code, nonce });
  }

  /**
   * Resends the MFA code
   * @param {String} nonce Nonce we got from response in login API and when MFA is enabled
   * @param {RidecellCallback} callback RidecellCallback for success or failure.
   *
   * @memberof Ridecell
   *
   * @example
   *
   * Ridecell.mfaResend({ nonce: '12323'})
   * .then(() => { }))
   * .catch((error) => { });
   */
  mfaResend({ nonce }) {
    return this.coreController.mfaResend({ nonce });
  }

  /**
   * Creates an event to get customer data from network.
   *
   * @param {CallBack} callback - Callback function for success and failure.
   * @memberof Ridecell
   *
   * @returns {Customer} Returns customer data.
   * @example
   *
   * Ridecell.getCustomer(callback)
   * .then((customer) => {})
   * .catch((error) => {});
   */
  getCustomer({ fetchUpdatedCustomer }) {
    return this.coreController.getCustomer({ fetchUpdatedCustomer });
  }

  /**
   * Logs out a user.
   *
   * @param {CallBack} callback - Callback function for success and failure.
   * @memberof Ridecell
   *
   * @example
   *
   * Ridecell.logout()
   * .then(() => {})
   * .catch((error) => {});
   */
  logout() {
    return this.coreController.logout();
  }

  /**
   * Synchronously gets the cached customer object.
   *
   * @returns {Customer} Returns customer data.
   * @memberof Ridecell
   * @example
   *
   * Ridecell.getCachedCustomer()
   */
  cachedCustomer() {
    return this.coreController.getCachedCustomer();
  }

  /**
   * Resets password for user.
   *
   * @param {String} phoneNumberOrEmail - Fully qualified phone number including country code or email address based on Settings.passwordResetNotificationType.
   * @param {CallBack} callback - Callback function for success and failure.
   * @returns {String[]} - List of messages from backend.
   * @memberof Ridecell
   *
   * @example
   * const callback = new RidecellCallback();
   * callback.onSuccess = (customer) => {};
   * callback.onFailure = (error) => {};
   *
   * if (settings.passwordResetNotificationType == "sms")
   *      Ridecell.resetPassword("+112222222222", callback);
   * else
   *      Ridecell.resetPassword("test@test.com", callback);
   */
  resetPassword(phoneNumberOrEmail) {
    return this.coreController.resetPassword(phoneNumberOrEmail);
  }

  resetPasswordVerify(data) {
    return this.coreController.resetPasswordVerify(data);
  }

  resetPasswordConfirm(data) {
    return this.coreController.resetPasswordConfirm(data);
  }

  /**
   * Gets the registration_allowed or not flag.
   *
   * @param {Number} lat - User location's lattitude
   * @param {Number} lng - User location's longitude
   * @param {String} tenantId - Tenant Id
   *
   * @memberof Ridecell
   *
   * @example
   * Ridecell.isRegistrationAllowed(16,16.2, 'dragon-qa')
   * .then(() => { }))
   * .catch((error) => { });
   */
  isRegistrationAllowed(lat, lng, tenantId) {
    return this.coreController.isRegistrationAllowed(lat, lng, tenantId);
  }

  /**
   * Gets the consent options for customer to accept
   * @param {Numeber} page - page number
   * @param {Number} pageSize - pageSize
   * @param {String} tenantId - Tenant Id
   *
   * @memberof Ridecell
   *
   * @example
   * Ridecell.getConsentOptions({ page: 1, page_size: 100, tenant_id: 'dragon-qa' })
   * .then(() => { }))
   * .catch((error) => { });
   */
  getConsentOptions(queryParams) {
    return this.coreController.getConsentOptions(queryParams);
  }

  /**
   * Gets the policy docs for customer
   *
   * @param {Numeber} page - page number
   * @param {Number} pageSize - pageSize
   * @param {String} tenantId - Tenant Id     *
   * @memberof Ridecell
   *
   * @example
   * Ridecell.getPolicyDocs({ page: 1, page_size: 100, tenant_id: 'dragon-qa' })
   * .then(() => { }))
   * .catch((error) => { });
   */
  getPolicyDocs(queryParams) {
    return this.coreController.getPolicyDocs(queryParams);
  }

  /**
   * Creates the customer
   *
   * @param {Object} data - Customer object with all the required field
   * 
   * @memberof Ridecell
   *
   * @example
   * 
   *	const customerData = {
      business_profile_activation_code: '',
      consent_decisions: [
        {
        consent_option: 345,
        option_version: 1,
        user_decision: true 
        }
      ],
      email: 'srikanth.margam+app@ridecell.com',
      first_name: 'srikanth',
      last_name: 'Margam',
      password: 'Srikanth@007',
      phone_number: '+9163011XXXXX',
      policy_document_agreements: [
        {
        document_version: 1,
        policy_document: 218
        }
      ],
      registration_lat: 18.1,
      registration_lng: 16.2
    }
   * Ridecell.createCustomer(customerData)
   * .then(() => { }))
   * .catch((error) => { });
   */
  createCustomer(data) {
    return this.coreController.createCustomer(data);
  }

  /**
   * Verify the phone number
   *
   * @param {String} id - Customer Id
   * @param {Object} data - Data should contain the verification_code key
   *
   * @memberof Ridecell
   *
   * @example
   * const data = { verification_code: '1234' }
   * Ridecell.verifyPhoneNumber(data)
   * .then(() => { }))
   * .catch((error) => { });
   */
  verifyPhoneNumber({ customerId, data }) {
    return this.coreController.verifyPhoneNumber({ customerId, data });
  }

  /**
   * It fetchs the registration steps
   *
   * @param {Object} id - Customer Id
   * @param {String} tenantId - Tenant Id
   * @memberof Ridecell
   *
   * @example
   * Ridecell.getRegistrationSteps({ customerId: 344, queryParams: { tenant_id: 'dragon-qa'})
   * .then(() => { }))
   * .catch((error) => { });
   */
  getRegistrationSteps({ customerId, queryParams }) {
    return this.coreController.getRegistrationSteps({
      customerId,
      queryParams,
    });
  }

  /**
   * It updates the customer
   *
   * @param {Object} id - Customer Id
   * @param {String} tenantId - Tenant Id
   *@memberof Ridecell
   *
   * @example
   * Ridecell.getRegistrationSteps({ customerId: 344, queryParams: { tenant_id: 'dragon-qa'})
   * .then(() => { }))
   * .catch((error) => { });
   */
  updateCustomer({ customerId, data }) {
    return this.coreController.updateCustomer({ customerId, data });
  }

  /**
   * Getting the registration Info
   *
   * @param {Number} lat - lattitude
   * @param {lng} lng - Longitude
   * @param {Number} customerId -Customer Id
   * @param {String} lng - Product type
   *
   * @memberof Ridecell
   *
   * @example
   *
   * Ridecell.getRegistrationInfo(3,4, 433, 'CARSHARING')
   * .then(() => { }))
   * .catch((error) => { });
   */

  getRegistrationInfo(lat, lng, customerId, productType) {
    return this.coreController.getRegistrationInfo(
      lat,
      lng,
      customerId,
      productType
    );
  }

  /**
   * Saving the mailing address of customer
   * 
   * @param {Number} customerId -Customer Id
   * @param {Object} data - required data for saving the mailing address
   *  
   * @memberof Ridecell
   *
   * @example
   * const data = {
   *  local: 'en-Us',
    mailing_address_display: '900 Pune Maharashtra'
   * }
   * Ridecell.saveMailingAddress({ customerId, data })
   * .then(() => { }))
   * .catch((error) => { });
   */
  saveMailingAddress({ customerId, data }) {
    return this.coreController.saveMailingAddress({ customerId, data });
  }

  /**
   * Saves the customer's driver license
   * 
   * @param {Number} customerId -Customer Id
   * @param {Object} data - required data for saving the mailing address
   *  
   * @memberof Ridecell
   *
   * @example
   * const data = {
    'date_of_birth': '1998-12-12',
    'license_expiry_date': '2029-12-12',
    'license_issued_state': 'NYC',
    license_number: '1234',
   * }
   * Ridecell.saveDriverLicense({ customerId: 433, data})
   * .then(() => { }))
   * .catch((error) => { });
   */
  saveDriverLicense({ customerId, data }) {
    return this.coreController.saveDriverLicense({ customerId, data });
  }

  /**
  * Uploads the document
  * 
  * @param {Number} customerId -Customer Id
  * @param {Object} data - required data for saving the mailing address
  * 
  * Note: Do not required to submit the formdata , This event does that once it gets the object
  * 
  * @memberof Ridecell
  *
  * @example
  * const data = {
      document_image: image file,
      document_specification_id: '2b84fd14-a479-11ec-b909-0242ac120002'
  * }
  * Ridecell.uploadDocuments({ customerId: 433, data })
  * .then(() => { }))
  * .catch((error) => { });
  */
  uploadDocuments({ customerId, data }) {
    return this.coreController.uploadDocuments({ customerId, data });
  }

  /**
   * Uploads the document
   * 
   * @param {Number} customerId -Customer Id
   * @param {Object} data - Object must have key named 'completed_submission'
   *  
   *@memberof Ridecell
   *
   * @example
   * const data = {
       completed_submission: true
   * }
   * Ridecell.uploadDocuments({ customerId: 433, data })
   * .then(() => { }))
   * .catch((error) => { });
   */
  completeDocumentSubmission({ customerId, data }) {
    return this.coreController.completeDocumentSubmission({ customerId, data });
  }

  /**
  * Resends the verification code
  * 
  * @param {Number} customerId -Customer Id
  * @param {Object} data - Object should inlude either of phone_number or email key
  *  
  * @memberof Ridecell
  *
  * @example
  * const data = {
      phone_number/email: '+91630XXXXXX/test@ridecell.com'
  * }
  * Ridecell.resendVerificationCode({ customerId: 433, data })
  * .then(() => { }))
  * .catch((error) => { });
  */
  resendVerificationCode({ customerId, data }) {
    return this.coreController.resendVerificationCode({ customerId, data });
  }

  /**
   * Creates the consent
   *
   * @param {String} userId - Consent User Id
   * @param {String} tenantId - Tenant Id
   * @param {Array} data - Consent options array
   *
   * @memberof Ridecell
   *
   * @example
   *
   * const consentDecisions = []
   * Ridecell.createConsent('5546ed36-3218-45f6-b433-ab4e312569f2', 'dev6-dev', consentDecisions)
   * .then(() => { }))
   * .catch((error) => { });
   */
  createConsent({ data, queryParams }) {
    return this.coreController.createConsent({ data, queryParams });
  }

  /**
   * Creates the policy agreements
   *
   * @param {String} userId -  User Id
   * @param {String} tenantId - Tenant Id
   * @param {Array} data - policy documents array
   *
   * @memberof Ridecell
   *
   * @example
   *
   * const policyDecisions = []
   * Ridecell.createPolicy('5546ed36-3218-45f6-b433-ab4e312569f2', 'dev6-dev', policyDecisions)
   * .then(() => { }))
   * .catch((error) => { });
   */
  createPolicy({ data, queryParams }) {
    return this.coreController.createPolicy({ data, queryParams });
  }

  /**
   * Gets the policy agreements on which user action is required
   *
   * @param {String} userId -  User Id
   * @param {String} tenantId - Tenant Id
   *
   * @memberof Ridecell
   *
   * @example
   *
   * Ridecell.getActionRequiredPolicy('5546ed36-3218-45f6-b433-ab4e312569f2', 'dev6-dev')
   * .then(() => { }))
   * .catch((error) => { });
   */
  getActionRequiredPolicy(queryParams) {
    return this.coreController.getActionRequiredPolicy(queryParams);
  }

  /**
  * Links business profile
  * 
  * @param {Number} customerId -Customer Id
  * @param {Object} data - Object should include business profile activation code
  *  
  * @memberof Ridecell
  *
  * @example
  * const data = {
      "business_profile_activation_code":"D92AZ6"
  * }
  * Ridecell.linkBusinessProfile({ customerId: 433, data })
  * .then(() => { }))
  * .catch((error) => { });
  */
  linkBusinessProfile({ customerId, data }) {
    return this.coreController.linkBusinessProfile({ customerId, data });
  }

  /**
   * Fetches document specification required for MIV
   *
   * @param {CallBack} callback - Callback function for success and failure.
   * @memberof Ridecell
   *
   * @example
   * Ridecell.getDocumentSpecificationForMIV()
   * .then(() => { })
   * .catch((error) => { });
   *
   */
  getDocumentSpecificationForMIV() {
    return this.coreController.getDocumentSpecificationForMIV();
  }

  /**
   * Saving the mailing address of customer
   * 
   * @param {Number} customerId -Customer Id
   * @param {Object} data - required data for saving the mailing address
   *  
   * @memberof Ridecell
   *
   * @example
   * const data = {
   *  local: 'en-Us',
      mailing_address_display: ''
   * }
   * Ridecell.deleteMailingAddress({ customerId: 433, data })
   * .then(() => { }))
   * .catch((error) => { });
   */
  deleteMailingAddress(id) {
    return this.coreController.deleteMailingAddress(id);
  }

  /**
   * Generating Veriff Session URL
   *
   * @param {Number} customerId -Customer Id
   *
   * @memberof Ridecell
   *
   * @example
   * Ridecell.createVeriffSession({ customerId: 433 })
   * .then(() => { }))
   * .catch((error) => { });
   */
  createVeriffSession(id) {
    return this.coreController.createVeriffSession(id);
  }

  /**
   * Get Registration Status
   *
   * @param {Number} customerId -Customer Id
   *
   * @memberof Ridecell
   *
   * @example
   * Ridecell.getRegistrationStatus({ customerId: 433 })
   * .then(() => { }))
   * .catch((error) => { });
   */
  getRegistrationStatus(id) {
    return this.coreController.getRegistrationStatus(id);
  }

  /**
   * API to fetch the paymentIntent for a customer
   *
   * @param {Object} data - required data for fetching the payment sheet for stripe
   *
   * @memberof Ridecell
   *
   * @example
   * const data = {
   *  customer_id: '123',
   * }
   * Ridecell.fetchPaymentSheet(data)
   * .then(() => { }))
   * .catch((error) => { });
   */

  fetchPaymentSheet(data) {
    return this.coreController.fetchPaymentSheet(data);
  }

  /**
   *  API to fetch the cards of a customer
   *
   * @param {String} id - customer_id
   *
   * @memberof Ridecell
   *
   * @example
   * Ridecell.getCards('1223')
   * .then(() => { }))
   * .catch((error) => { });
   */

  getCards(id) {
    return this.coreController.getCards(id);
  }

  /**
   *  API to delete the customer card
   *
   * @param {String} customerId - customer_id
   * @param {Object} data - Object contains the card_id
   *
   * @memberof Ridecell
   *
   * @example
   * const data = { card_id: 'pm_xsxjsyams' }
   * Ridecell.deleteCard({customerId: '123', data})
   * .then(() => { }))
   * .catch((error) => { });
   */

  deleteCard({ customerId, data }) {
    return this.coreController.deleteCard({ customerId, data });
  }

  /**
   *  API to mark the customer card as default
   *
   * @param {String} customerId - customer_id
   * @param {Object} data - Object contains the card_id
   *
   * @memberof Ridecell
   *
   * @example
   * const data = { card_id: 'pm_xsxjsyams' }
   * Ridecell.markCardAsDefault({customerId: '123', data})
   * .then(() => { }))
   * .catch((error) => { });
   */

  markCardAsDefault({ customerId, data }) {
    return this.coreController.markCardAsDefault({ customerId, data });
  }

  getAxeptaHostedPage({ customerId, data }) {
    return this.coreController.getAxeptaHostedPage({ customerId, data });
  }
}

/**
 * Ridecell singleton object for interacting with Ridecell components.
 * @namespace Ridecell
 */
export let Ridecell = new _Ridecell();
